import { useEffect, useState } from "react";
import { Row, Col, Form, Button, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar } from "react-bootstrap-icons";
import http from "services/httpClient";

interface StatDefinition {
  id: number;
  device_id: number;
  source_column_index: number;
  aggregation: "min" | "max" | "avg";
  window_hours: number;
  result_label: string;
  unit?: string;
}

interface DeviceLabel {
  column_index: number;
  label: string;
  multiplier?: number;
  offset?: number;
}

interface StatExportSectionColumn {
  stat_id: number;
  label: string;
  aggregation: "min" | "max" | "avg";
  window_hours: number;
  source_column_index: number;
  unit?: string;
}

interface StatExportSectionRow {
  bucket_start: string;
  bucket_end: string;
  values: Record<string, number | null>;
}

interface StatExportSection {
  window_hours: number;
  columns: StatExportSectionColumn[];
  rows: StatExportSectionRow[];
}

interface StatExportResponse {
  device_id: number;
  timezone: string;
  start: string;
  end: string;
  sections: StatExportSection[];
}

interface DeviceStatExportTabProps {
  deviceId: number;
  deviceName: string;
}

export function DeviceStatExportTab({
  deviceId,
  deviceName,
}: DeviceStatExportTabProps) {
  const [stats, setStats] = useState<StatDefinition[]>([]);
  const [deviceLabels, setDeviceLabels] = useState<DeviceLabel[]>([]);
  const [selectedStatIds, setSelectedStatIds] = useState<number[]>([]);
  const [exportStart, setExportStart] = useState<Date | null>(null);
  const [exportEnd, setExportEnd] = useState<Date | null>(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [tempStart, setTempStart] = useState<Date | null>(null);
  const [tempEnd, setTempEnd] = useState<Date | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchStats = async () => {
    try {
      const [statRes, labelRes] = await Promise.all([
        http.get(`/stat-definitions/device/${deviceId}`),
        http.get(`/device/${deviceId}/labels`),
      ]);
      setStats(statRes.data || []);
      setDeviceLabels(labelRes.data || []);
      setSelectedStatIds([]);
    } catch {
      setStats([]);
      setDeviceLabels([]);
      setSelectedStatIds([]);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [deviceId]);

  const getSourceColumnScale = (sourceColumnIndex: number): { multiplier: number; offset: number } => {
    const label = deviceLabels.find((l) => l.column_index === sourceColumnIndex);
    return { multiplier: label?.multiplier ?? 1, offset: label?.offset ?? 0 };
  };

  const formatDateForQuery = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;

  const selectedStats = stats.filter((s) => selectedStatIds.includes(s.id));

  return (
    <>
      <Row className="mb-3">
        <Col>
          <strong>Select Statistics:</strong>
          <div className="d-flex flex-wrap">
            {stats.map((stat) => {
              const label = `${stat.result_label}${stat.unit ? ` (${stat.unit})` : ""}`;

              return (
                <Form.Check
                  key={stat.id}
                  inline
                  label={label}
                  checked={selectedStatIds.includes(stat.id)}
                  onChange={() =>
                    setSelectedStatIds((prev) =>
                      prev.includes(stat.id)
                        ? prev.filter((i) => i !== stat.id)
                        : [...prev, stat.id]
                    )
                  }
                />
              );
            })}
          </div>

          {stats.length === 0 && (
            <div className="text-muted mt-2">
              No statistic definitions found for this device.
            </div>
          )}
        </Col>
      </Row>

      <Row className="mb-2 justify-content-between">
        <Col md="auto" className="d-flex align-items-end">
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center gap-2"
            onClick={() => {
              setTempStart(exportStart);
              setTempEnd(exportEnd);
              setShowCalendarModal(true);
            }}
          >
            <Calendar />
            {exportStart && exportEnd
              ? `${formatDateForQuery(exportStart)} → ${formatDateForQuery(exportEnd)}`
              : "Select Date Range"}
          </Button>
        </Col>

        <Col className="d-flex justify-content-end align-items-end">
          <Button
            onClick={async () => {
              if (!exportStart || !exportEnd || selectedStats.length === 0) return;

              setIsExporting(true);

              try {
                const params = new URLSearchParams();

                selectedStats.forEach((stat) => {
                  params.append("stat_ids", stat.id.toString());
                });

                params.append("start", formatDateForQuery(exportStart));
                params.append("end", formatDateForQuery(exportEnd));
                params.append(
                  "tz",
                  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
                );

                const res = await http.get<StatExportResponse>(
                  `/device-data/${deviceId}/stats_export?${params.toString()}`
                );

                const payload = res.data;

                if (!payload?.sections?.length) return;

                const now = new Date().toLocaleString().replace(/[:.]/g, "-");
                const filename = `${deviceName}_stats_${now}.csv`;

                const csvLines: string[] = [
                  `Device Name:,${deviceName}`,
                  `Export Type:,Statistics`,
                  `Range Start:,${payload.start}`,
                  `Range End:,${payload.end}`,
                  `Timezone:,${payload.timezone}`,
                  "",
                ];

                for (const section of payload.sections) {
                  if (!section.rows?.length) continue;

                  csvLines.push(`Window:,${section.window_hours} Hours`);

                  const header = [
                    "bucket_start",
                    "bucket_end",
                    ...section.columns.map((col) =>
                      `${col.label}${col.unit ? ` (${col.unit})` : ""}`
                    ),
                  ];

                  csvLines.push(header.join(","));

                  for (const row of section.rows) {
                    const line = [
                      row.bucket_start,
                      row.bucket_end,
                      ...section.columns.map((col) => {
                        const raw = row.values[col.stat_id.toString()];
                        if (raw === null || raw === undefined) return "";
                        const { multiplier, offset } = getSourceColumnScale(col.source_column_index);
                        const applied = raw * multiplier + offset;
                        return (Math.round(applied * 1000) / 1000).toString();
                      }),
                    ];

                    csvLines.push(line.join(","));
                  }

                  csvLines.push("");
                }

                const csvContent = csvLines.join("\n");

                const blob = new Blob([csvContent], {
                  type: "text/csv;charset=utf-8;",
                });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } catch (err) {
                console.error("Stat export error:", err);
              } finally {
                setIsExporting(false);
              }
            }}
            size="sm"
            variant="success"
            disabled={isExporting || stats.length === 0}
          >
            {isExporting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Exporting...
              </>
            ) : (
              "Export Stat CSV"
            )}
          </Button>
        </Col>
      </Row>

      <Modal
        show={showCalendarModal}
        onHide={() => setShowCalendarModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Export Date Range</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <DatePicker
              selected={tempStart}
              onChange={(date) => setTempStart(date)}
              showTimeSelect
              timeFormat="HH:mm:ss"
              timeIntervals={1}
              dateFormat="yyyy-MM-dd HH:mm:ss"
              timeCaption="Time"
              className="form-control"
              placeholderText="Start Date & Time"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <DatePicker
              selected={tempEnd}
              onChange={(date) => {
                if (date) {
                  setTempEnd(date);
                } else {
                  setTempEnd(null);
                }
              }}
              showTimeSelect
              timeFormat="HH:mm:ss"
              timeIntervals={1}
              dateFormat="yyyy-MM-dd HH:mm:ss"
              timeCaption="Time"
              className="form-control"
              placeholderText="End Date & Time"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCalendarModal(false)}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={() => {
              if (tempStart && tempEnd) {
                setExportStart(tempStart);
                setExportEnd(tempEnd);
              }
              setShowCalendarModal(false);
            }}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DeviceStatExportTab;